import { createColumnHelper, CellContext } from '@tanstack/react-table';
import { lt } from '@mews/framework';
import {
    ConvertedCurrencyValue,
    LocalizedDateTimeFromUtc,
    LocalizedUtcTimeUnit,
    LocalizedUtcTimeUnitFormat,
} from '@mews-commander/ui';
import { TimeUnit } from '@mews/enums';
import { OrderItem } from '@mews-commander/types';
import { Tag } from '@optimus-web/core';
import { PartialMonthDayRange } from '../PartialMonthDayRange';
import { BillLink } from '../../../../../components';

export interface TableMeta {
    timeUnit: TimeUnit;
    groupId: string;
    isMultiTaxEnvironment: boolean;
}

const DateCell: React.FC<CellContext<OrderItem, unknown>> = (info) => {
    const { proRataEndUtc, proRataStartUtc, startUtc } = info.row.original;
    const { timeUnit } = info.table.options.meta as TableMeta;
    return proRataStartUtc && proRataEndUtc ? (
        <PartialMonthDayRange proRataStartUtc={proRataStartUtc} proRataEndUtc={proRataEndUtc} showDaysCount />
    ) : (
        <LocalizedUtcTimeUnit
            format={LocalizedUtcTimeUnitFormat.Long}
            timeUnit={timeUnit}
            utcString={startUtc as string}
        />
    );
};

const TotalAmountCell: React.FC<CellContext<OrderItem, OrderItem['totalAmount']>> = (info) => {
    const { isMultiTaxEnvironment } = info.table.options.meta as TableMeta;

    return <ConvertedCurrencyValue amount={info.getValue()} net={isMultiTaxEnvironment} />;
};

const IdCell: React.FC<CellContext<OrderItem, OrderItem['id']>> = (info) => {
    const id = info.getValue();
    const { groupId } = info.table.options.meta as TableMeta;
    return <BillLink orderItemId={id} groupId={groupId} />;
};

const columnHelper = createColumnHelper<OrderItem>();

export const simpleColumns = [
    columnHelper.accessor('startUtc', {
        header: () => lt('Date'),
        cell: DateCell,
    }),
    columnHelper.accessor('totalAmount', {
        header: () => lt('Cost'),
        cell: TotalAmountCell,
        meta: {
            align: 'end',
        },
    }),
    columnHelper.accessor('consumedUtc', {
        header: () => lt('Consumed'),
        cell: (info) => <LocalizedDateTimeFromUtc utcString={info.getValue()} />,
    }),
    columnHelper.accessor('id', {
        header: () => lt('Bill'),
        cell: IdCell,
    }),
    columnHelper.accessor('isCanceled', {
        header: () => ' ',
        cell: (info) => (info.getValue() ? <Tag variant="danger">{lt('Deleted')}</Tag> : null),
    }),
];
